import { ArrowUpRight, Copy, Key, Plus, Robot } from '@phosphor-icons/react';
import {
  getListAgentConnectionsQueryKey,
  getListApiKeysQueryKey,
  useListAgentConnections,
  useListApiKeys,
  useRevokeAgentConnection,
  useRevokeApiKey,
} from '@reelplicate/api-client/agent-access';
import type { AgentConnectionDto, ApiKeyDto } from '@reelplicate/api-client/model';
import { useListMembers } from '@reelplicate/api-client/team';
import { useListWorkspaces } from '@reelplicate/api-client/workspaces';
import {
  Badge,
  BrandMark,
  Button,
  ConfirmDialog,
  IconButton,
  Skeleton,
  toast,
} from '@reelplicate/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { relativeTime } from '@/features/notifications/to-entry';
import { marketingUrl } from '@/lib/marketing';

import {
  AGENT_ROLES,
  agentMark,
  grantableRoles,
  MCP_URL,
  MCP_URL_LABEL,
  shortDate,
} from './agents';
import { CreateApiKeyModal } from './create-api-key-modal';
import {
  ListRow,
  SettingsCard,
  SettingsPageLayout,
  SettingsRow,
  SettingsSection,
} from './settings-layout';
import { WebhooksSection } from './webhooks-section';

type Revoking =
  { kind: 'agent'; item: AgentConnectionDto } | { kind: 'key'; item: ApiKeyDto };

const personName = (person: { name: string | null; email: string | null }) =>
  person.name ?? person.email ?? 'someone';

const lastUsed = (at: string | null) =>
  at
    ? `last used ${relativeTime(new Date(at), new Date()).toLowerCase()}`
    : 'not used yet';

/**
 * Figma "Settings — API & MCP" (322:1414): the MCP server URL, agents
 * connected through it, API keys, and webhooks. Owners see and revoke
 * everyone's agents and keys; other members their own.
 */
export function ApiPage() {
  const queryClient = useQueryClient();
  const { data: workspaces } = useListWorkspaces();
  const workspace = workspaces?.[0];
  const workspaceId = workspace?.id ?? '';
  const enabled = { query: { enabled: Boolean(workspace) } };
  const { data: agents } = useListAgentConnections(workspaceId, enabled);
  const { data: keys } = useListApiKeys(workspaceId, enabled);
  const { data: members } = useListMembers(workspaceId, enabled);
  const myRole = members?.find((member) => member.isYou)?.role;
  const revokeAgent = useRevokeAgentConnection();
  const revokeKey = useRevokeApiKey();
  const [creating, setCreating] = useState(false);
  // A fresh dialog per opening, so a shown key never comes back.
  const [createCount, setCreateCount] = useState(0);
  const [revoking, setRevoking] = useState<Revoking | null>(null);

  const revoke = async ({ kind, item }: Revoking) => {
    try {
      if (kind === 'agent') {
        await revokeAgent.mutateAsync({ id: workspaceId, connectionId: item.id });
      } else {
        await revokeKey.mutateAsync({ id: workspaceId, keyId: item.id });
      }
    } catch {
      return; // The failure is already shown as a toast.
    }
    await queryClient.invalidateQueries({
      queryKey:
        kind === 'agent'
          ? getListAgentConnectionsQueryKey(workspaceId)
          : getListApiKeysQueryKey(workspaceId),
    });
    toast.success(`${kind === 'agent' ? item.agentName : item.name} revoked`);
  };

  const revokingName =
    revoking?.kind === 'agent' ? revoking.item.agentName : revoking?.item.name;

  return (
    <SettingsPageLayout
      title="API & MCP"
      description={`Let agents and scripts work in ${workspace?.name ?? 'your workspace'}. Each connection acts with the role you give it — never more.`}
      actions={
        <Button asChild variant="ghost" leadingIcon={ArrowUpRight}>
          <a href={marketingUrl('/docs/mcp')} target="_blank" rel="noreferrer">
            Read the docs
          </a>
        </Button>
      }
    >
      <SettingsSection
        title="MCP server"
        description="Paste this URL into Claude, ChatGPT, Cursor or any MCP client, then sign in to approve it."
      >
        <SettingsCard>
          <SettingsRow
            label="Server URL"
            description="Streamable HTTP · OAuth sign-in or an API key"
            control={
              <>
                <span className="text-data-md text-text-primary">{MCP_URL_LABEL}</span>
                <IconButton
                  icon={Copy}
                  size="sm"
                  variant="ghost"
                  aria-label="Copy server URL"
                  onClick={() => {
                    void navigator.clipboard?.writeText(MCP_URL);
                    toast.success('Server URL copied');
                  }}
                />
              </>
            }
          />
        </SettingsCard>
      </SettingsSection>

      <SettingsSection
        title="Connected agents"
        description="Agents act as the person who approved them, limited to the role shown."
      >
        <SettingsCard>
          {!agents ? (
            <Skeleton className="my-4 h-10" />
          ) : agents.length === 0 ? (
            <SettingsRow
              label="No agents yet"
              description="Add the server URL to an MCP client and sign in to connect one."
            />
          ) : (
            agents.map((agent) => {
              const mark = agentMark(agent.agentName);
              return (
                <ListRow
                  key={agent.id}
                  tile={
                    mark ? (
                      <BrandMark name={mark} size={20} />
                    ) : (
                      <Robot aria-hidden className="text-icon-primary size-5" />
                    )
                  }
                  title={agent.agentName}
                  meta={`Approved by ${agent.isYours ? 'you' : personName(agent.approvedBy)} on ${shortDate(agent.approvedAt)} · ${lastUsed(agent.lastUsedAt)}`}
                >
                  <Badge>{AGENT_ROLES[agent.role].name}</Badge>
                  <Button
                    variant="outline"
                    size="sm"
                    aria-label={`Revoke ${agent.agentName}`}
                    onClick={() => setRevoking({ kind: 'agent', item: agent })}
                  >
                    Revoke
                  </Button>
                </ListRow>
              );
            })
          )}
        </SettingsCard>
      </SettingsSection>

      <SettingsSection
        title="API keys"
        description="For scripts and automations like Zapier or n8n. We only show a key once."
        action={
          <Button
            variant="outline"
            size="sm"
            leadingIcon={Plus}
            disabled={!myRole}
            onClick={() => {
              setCreateCount((n) => n + 1);
              setCreating(true);
            }}
          >
            Create key
          </Button>
        }
      >
        <SettingsCard>
          {!keys ? (
            <Skeleton className="my-4 h-10" />
          ) : keys.length === 0 ? (
            <SettingsRow
              label="No API keys yet"
              description="Create one for an automation that can’t sign in."
            />
          ) : (
            keys.map((key) => (
              <ListRow
                key={key.id}
                tile={<Key aria-hidden className="text-icon-primary size-5" />}
                title={key.name}
                meta={`Created ${shortDate(key.createdAt)} by ${key.isYours ? 'you' : personName(key.createdBy)} · ${lastUsed(key.lastUsedAt)}`}
              >
                <span className="text-data-md text-text-secondary hidden sm:inline">
                  {key.preview}
                </span>
                <Badge>{AGENT_ROLES[key.role].name}</Badge>
                <Button
                  variant="outline"
                  size="sm"
                  aria-label={`Revoke ${key.name}`}
                  onClick={() => setRevoking({ kind: 'key', item: key })}
                >
                  Revoke
                </Button>
              </ListRow>
            ))
          )}
        </SettingsCard>
      </SettingsSection>

      <WebhooksSection
        workspaceId={workspaceId}
        canManage={myRole === 'OWNER' || myRole === 'EDITOR'}
      />

      {workspace && (
        <CreateApiKeyModal
          key={createCount}
          workspaceId={workspaceId}
          roles={grantableRoles(myRole)}
          open={creating}
          onOpenChange={setCreating}
        />
      )}

      <ConfirmDialog
        open={revoking !== null}
        onOpenChange={(open) => !open && setRevoking(null)}
        title={`Revoke ${revokingName}?`}
        description={
          revoking?.kind === 'agent'
            ? `${revokingName} is signed out straight away. Connecting it again needs a new approval.`
            : 'Anything using this key stops working straight away.'
        }
        confirmLabel="Revoke"
        onConfirm={() => revoking && void revoke(revoking)}
      />
    </SettingsPageLayout>
  );
}
